import React, { useContext, useEffect, useLayoutEffect, useMemo, useReducer, useState } from 'react'
import { lockedReducer } from '../Utils/reducers'
import { createMonthArr, createWeekArr } from './dayarrays'
import dayjs from 'dayjs'
import { Box } from '@mui/material'
import './styles.css'
import { externalComponent } from '../AppRoutes'
import { api } from '../../services/api'
import { capitalize } from '../Utils/functions'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ColorButton from '../Buttons/ColorButton'
import WorkIcon from '@mui/icons-material/Work'
import EngineeringIcon from '@mui/icons-material/Engineering'
import TourIcon from '@mui/icons-material/Tour'
import 'dayjs/locale/pt-br'
import MonthCell from './month-cell'
import apptheme from '../theme/default'
import { getSession } from '../../contexts/auth'
import EditScheduleModal from '../Cockpit/Schedule/Edit-schedule-modal'
import EditProposalModal from '../Cockpit/Proposal/Edit-proposal-modal'
import EditServiceOrderModal from '../Cockpit/ServiceOrder/Edit-serviceorder-modal'
import DayScheduleModal from './dayschedulemodal'
import DayCell from './day-cell'
import { ContactSupportOutlined } from '@mui/icons-material'
import toast from 'react-hot-toast'

dayjs.locale('pt-BR')

const weekdays = {
    'DOM': 'Domingo',
    'SEG': 'Segunda',
    'TER': 'Terça',
    'QUA': 'Quarta',
    'QUI': 'Quinta',
    'SEX': 'Sexta',
    'SAB': 'Sábado',
}

export const avatars = {
    'schedule': (<TourIcon />),
    'proposal': (<WorkIcon />),
    'serviceorder': (<EngineeringIcon />)
}

export const colors = {
    'OPEN': '#ffa500',
    'SCHEDULED': '#0d6efd',
    'DONE': '#24a024',
    'CANCELLED': '#dc3545',
}

export const translations = {
    'OPEN': 'Em aberto',
    'SCHEDULED': 'Agendado',
    'DONE': 'Concluído',
    'CANCELLED': 'Cancelado',
    'schedule': 'Agendamento de visita',
    'proposal': 'Proposta',
    'serviceorder': 'Ordem de serviço'
}

const getData = async (startDate, endDate, setLoader, arr) => {
    setLoader(true)

    var start = arr[0]
    var end = arr[arr.length - 1]
    const querysearch = new URLSearchParams()
    querysearch.append('datetime__gte', start.format('YYYY-MM-DD 00:00Z'))
    querysearch.append('datetime__lte', end.format('YYYY-MM-DD 23:59Z'))

    try {
        const [schedule, proposal, serviceorder, item_type] = await Promise.all([
            api.get('/order/schedule/?' + querysearch.toString()),
            api.get('/order/proposal/?' + querysearch.toString()),
            api.get('/order/serviceorder/?' + querysearch.toString()),
            api.get('/order/proposal/item_types/')
        ])

        const respArr = []

        if (schedule.status === 200)
            respArr.push(...schedule.data.results.map(each => ({
                ...each,
                type: 'schedule',
                format_datetime: dayjs(each.datetime).format('YYYYMMDD')
            })))

        if (proposal.status === 200)
            respArr.push(...proposal.data.results.map(each => ({
                ...each,
                type: 'proposal',
                format_datetime: dayjs(each.datetime).format('YYYYMMDD')
            })))

        if (serviceorder.status === 200)
            respArr.push(...serviceorder.data.results.map(each => ({
                ...each,
                type: 'serviceorder',
                format_datetime: dayjs(each.datetime).format('YYYYMMDD')
            })))

        setLoader(false)

        return {
            data: respArr,
            item_types: item_type.status === 200 ? item_type.data : {}
        }
    } catch (error) {
        setLoader(false)
        return { data: [], item_types: {} }
    }
}


const mergeData = (existingData, newData) => {
    const dataMap = new Map()

    if (Array.isArray(existingData)) {
        existingData.forEach(item => {
            const key = `${item.type}_${item.id}`
            dataMap.set(key, item)
        })
    }

    if (Array.isArray(newData)) {
        newData.forEach(item => {
            const key = `${item.type}_${item.id}`
            dataMap.set(key, item)
        })
    }

    return Array.from(dataMap.values())
}

const initialize = async (selectedDay, view, setState, setLoader, allData, setAllData, state) => {

    const month = selectedDay.month()
    const year = selectedDay.year()

    const key = `${month}-${year}`

    if (view === 'day') {
        if (state.loadedMonthKey === key) {
            return
        }
    }

    const arr = createMonthArr(selectedDay.$M, selectedDay.$y)

    const startDate = arr[0]
    const endDate = arr[arr.length - 1]

    const result = await getData(startDate, endDate, setLoader, arr)
    const mergedData = mergeData(allData, result.data)

    setAllData(mergedData)
    setState({
        arr,
        data: mergedData,
        item_types: result.item_types,
        loadedMonthKey: key
    })
}

const initialFilters = {
    ...Object.fromEntries(Object.entries(avatars).map(([key, value]) => [key, { icon: value, active: true }])),
    ...Object.fromEntries(Object.entries(colors).map(([key, value]) => [key, { color: value, active: true }]))
}

const Calendar = () => {
    const user = getSession()
    const { setLoader } = useContext(externalComponent)
    const [allData, setAllData] = useState([])

    const [refreshKey, setRefreshKey] = useState(0)

    const [state, setState] = useReducer(lockedReducer, {
        selectedDay: dayjs(),
        view: 'month',
        arr: [],
        data: [],
        item_types: {},
        showFilters: false,
        editSchedule: null,
        editProposal: null,
        editServiceOrder: null,
        showDaySchedule: false,
        cloneRow: null,
        showTableDay: false,
        loadedMonthKey: ''
    })

    const [filters, setFilters] = useReducer(lockedReducer, {
        ...initialFilters
    })

    const activeFilters = useMemo(() =>
        Object.entries(filters).filter(([, v]) => v.active).map(([k]) => k), [filters])

    const weekdays_list = useMemo(() => Object.entries(weekdays), [])

    const reloadData = async () => {

        let rangeStart, rangeEnd

        if (state.view === 'day') {
            rangeStart = state.selectedDay.subtract(30, 'days')
            rangeEnd = state.selectedDay.add(30, 'days')
        } else {
            rangeStart = state.selectedDay.startOf('month').subtract(15, 'days')
            rangeEnd = state.selectedDay.endOf('month').add(15, 'days')
        }

        const result = await getData(rangeStart, rangeEnd, setLoader, state.arr)
        const mergedData = mergeData(allData, result.data)

        setAllData(mergedData)

        const arr = state.view === 'month'
            ? createMonthArr(state.selectedDay.$M, state.selectedDay.$y)
            : createWeekArr(state.selectedDay.$D, state.selectedDay.$M, state.selectedDay.$y)

        setState({
            arr,
            data: mergedData,
            item_types: result.item_types
        })

        setRefreshKey(prev => prev + 1)
    }

    const updateSelectedDay = (newDay, newView = null) => {
        const targetView = newView || state.view

        setState({
            selectedDay: newDay,
            view: targetView
        })

        const needsMoreData = !Array.isArray(allData) || allData.length === 0 ||
            !allData.some(item => {
                const itemDate = dayjs(item.datetime)
                const daysDiff = Math.abs(itemDate.diff(newDay, 'days'))
                return daysDiff <= 7
            })

        //     // if (state.arr[state.arr.length - 1] >= state.arr[state.arr.length - 1]) {
        //     //     setTimeout(() => {
        //     //         initialize(newDay, targetView, setState, setLoader, allData, setAllData, state)
        //     //     }, 50)
        // }
    }

    useLayoutEffect(() => {
        initialize(state.selectedDay, state.view, setState, setLoader, allData, setAllData, state)
    }, [state.selectedDay.format('YYYY-MM'), state.view])

    useLayoutEffect(() => {
        const saved = localStorage.getItem('calendar-filter')
        if (saved) {
            try {
                const parsed = JSON.parse(saved)
                const newfilters = Object.fromEntries(Object.entries(initialFilters).map(([k, v]) =>
                    [k, { ...v, active: parsed.includes(k) }]
                ))
                setFilters(newfilters)
            } catch (error) {
                toast.error('Erro ao carregar filtros salvos:', error)
            }
        }
    }, [])

    useEffect(() => {
        const todayElement = document.getElementsByClassName('today')[0]
        if (todayElement) {
            todayElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
    }, [state.arr])

    return (
        <>
            {state.editSchedule &&
                <EditScheduleModal
                    data={state.editSchedule}
                    handleClose={() => setState({ editSchedule: null })}
                    reloadData={reloadData}
                    openProposal={forward => setState({ editSchedule: null, editProposal: forward })}
                />}
            {state.editProposal &&
                <EditProposalModal
                    data={state.editProposal}
                    item_types={state.item_types}
                    handleClose={() => setState({ editProposal: null })}
                    reloadData={reloadData}
                    openOS={forward => setState({ editProposal: null, editServiceOrder: forward })}
                />}
            {state.editServiceOrder &&
                <EditServiceOrderModal
                    data={state.editServiceOrder}
                    handleClose={() => setState({ editServiceOrder: null })}
                    reloadData={reloadData}
                    openOS={forward => setState({ editServiceOrder: forward })}
                    cloneRow={(data) => setState({ cloneRow: data })}
                    typeModal={true}
                />
            }
            {state.cloneRow &&
                <EditServiceOrderModal
                    data={state.cloneRow}
                    handleClose={() => setState({ cloneRow: null })}
                    reloadData={reloadData}
                    openOS={forward => setState({ editServiceOrder: forward })}
                    cloneRow={(data) => setState({ cloneRow: data })}
                    typeModal={false}
                />
            }
            {state.showDaySchedule &&
                <DayScheduleModal handleClose={() => setState({ showDaySchedule: false })} />
            }

            <Box className="calendar-main-frame">
                <Box className="left" sx={{ display: 'flex', gap: '1rem' }}>
                    <ColorButton
                        disabled={!user.permissions.add_schedule}
                        onClick={() => setState({ editSchedule: {} })}
                    >
                        Nova visita
                    </ColorButton>
                    <ColorButton
                        reverse
                        border="1px solid #d0d0d0"
                        onClick={() => setState({ showDaySchedule: true })}
                    >
                        Agenda do dia
                    </ColorButton>
                    <ColorButton
                        reverse={!state.showFilters}
                        border="1px solid #d0d0d0"
                        onClick={() => setState({ showFilters: !state.showFilters })}
                    >
                        Ver Filtros
                    </ColorButton>
                </Box>

                {state.showFilters &&
                    <Box>
                        <h6>Filtros</h6>
                        <Box className="calendar-inlineflex">
                            {Object.entries(filters).map(([key, value]) =>
                                <ColorButton
                                    key={key}
                                    hColor={apptheme.quaternaryColor}
                                    startIcon={value.icon || <Box className="inlineicon" sx={{ backgroundColor: value.color }}></Box>}
                                    reverse={!value.active}
                                    onClick={() => {
                                        const updated = { [key]: { ...value, active: !value.active } }
                                        setFilters(updated)
                                        const filterString = Object.entries({ ...filters, ...updated })
                                            .filter(([, val]) => val.active)
                                            .map(([k]) => k)
                                        localStorage.setItem('calendar-filter', JSON.stringify(filterString))
                                    }}
                                >
                                    {translations[key]}
                                </ColorButton>
                            )}
                        </Box>
                    </Box>
                }

                <Box className="calendar-wrapper">
                    {!state.showTableDay ? (
                        <>
                            <Box className="select-month-row">
                                <ColorButton onClick={() => updateSelectedDay(state.selectedDay.subtract(1, 'month'))}>
                                    <ArrowBackIcon />
                                </ColorButton>
                                <Box>{capitalize(state.selectedDay.format('MMMM YYYY'))}</Box>
                                <ColorButton onClick={() => updateSelectedDay(state.selectedDay.add(1, 'month'))}>
                                    <ArrowForwardIcon />
                                </ColorButton>
                            </Box>

                            <Box className="month-list-header">
                                {weekdays_list.map(([abbr]) => (
                                    <Box key={abbr} className="month-header">{abbr}</Box>
                                ))}
                            </Box>

                            <Box className="month-list">
                                {state.arr.map(day => (
                                    <Box
                                        key={day.format('YYYYMMDD')}
                                        className={`month-cell ${day.$M !== state.selectedDay.$M ? 'otherday' : ''} ${day.format('YYYYMMDD') === dayjs().format('YYYYMMDD') ? 'today' : ''}`}
                                        onClick={() => setState({ selectedDay: day, view: 'day', showTableDay: true })}
                                    >
                                        <Box className="month-items">
                                            {day.format('DD')}
                                            <MonthCell openItem={setState}>
                                                {state.data
                                                    .filter(order =>
                                                        activeFilters.includes(order.type) &&
                                                        activeFilters.includes(order.state) &&
                                                        order.format_datetime === day.format('YYYYMMDD')
                                                    )
                                                    .sort((x, y) => dayjs(x.datetime).valueOf() - dayjs(y.datetime).valueOf())}
                                            </MonthCell>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </>
                    ) : (
                        <>
                            <Box className="select-month-row">
                                <ColorButton onClick={() => setState({ view: 'month', showTableDay: false })}>
                                    <ArrowBackIcon /> Voltar
                                </ColorButton>
                            </Box>
                            <DayCell
                                key={`${state.selectedDay.format('YYYY-MM-DD')}-${refreshKey}`}
                                parent={state.data}
                                data={allData}
                                initialDate={state.selectedDay}
                                openItem={(updates) => {
                                    if (updates.selectedDay) {
                                        updateSelectedDay(updates.selectedDay, 'day')
                                    } else {
                                        setState(updates)
                                    }
                                }}
                                filter={filters}
                                reloadData={reloadData}
                            />
                        </>
                    )}
                </Box>
            </Box>
        </>
    )
}

export default Calendar